import React, { useState, useRef, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'

import { RESOURCE_TYPE_COMPONENTS } from '../resourceTypeComponents'

import Container from '@mui/material/Container'
import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import Avatar from '@mui/material/Avatar'
import Typography from '@mui/material/Typography'
import TextField from '@mui/material/TextField'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Divider from '@mui/material/Divider'
import Alert from '@mui/material/Alert'
import CircularProgress from '@mui/material/CircularProgress'
import { keyframes } from '@mui/system'
import SendIcon from '@mui/icons-material/Send'
import SmartToyIcon from '@mui/icons-material/SmartToy'
import PersonIcon from '@mui/icons-material/Person'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'

import { setErrorNotification } from '../../reducers/notificationReducer'
import chatbotService from '../../services/chatbot'
import { PAGES } from '../navigation/routes'

// Animated "typing" dots shown while the assistant is composing a reply.
const typingBounce = keyframes`
  0%, 80%, 100% { transform: scale(0.5); opacity: 0.35; }
  40% { transform: scale(1); opacity: 1; }
`

// Blinking caret shown at the end of the reply while it streams in token by token.
const caretBlink = keyframes`
  0%, 50% { opacity: 1; }
  50.01%, 100% { opacity: 0; }
`

const StreamingReply = ({ text }) => (
    <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
        {text}
        <Box
            component="span"
            sx={{
                display: 'inline-block', width: '2px', height: '1.1em', ml: '2px',
                verticalAlign: 'text-bottom', bgcolor: 'primary.main',
                animation: `${caretBlink} 1s step-end infinite`,
            }}
        />
    </Typography>
)

const TypingDots = () => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, py: 0.75 }}>
        {[0, 1, 2].map((i) => (
            <Box
                key={i}
                sx={{
                    width: 8, height: 8, borderRadius: '50%', bgcolor: 'primary.main',
                    animation: `${typingBounce} 1.2s infinite ease-in-out`,
                    animationDelay: `${i * 0.2}s`,
                }}
            />
        ))}
    </Box>
)

const SUGGESTIONS = [
    'I am a beginner interested in deep learning for medical imaging',
    'Help me learn about clinical decision support systems',
    'I want to understand AI ethics and policy in healthcare',
]

// The null stub prefixes step titles with "Step N:"; strip it so the number badge isn't doubled.
const cleanTitle = (t) => (t || '').replace(/^step\s*\d+:\s*/i, '').trim() || 'Recommended resources'

// Short detail line for a trace event (e.g. the focus areas the intent stage identified).
const traceDetail = (ev) => {
    if (ev.stage === 'intent_done' && ev.detail && ev.detail.topics && ev.detail.topics.length) {
        return ev.detail.topics.join(', ')
    }
    return ''
}

// The chatbot returns full serialized resources keyed on `type`. Render each as a compact vertical
// LIST row (the same components Recommended.js uses) so a plan reads as an ordered list.
const renderListItem = (resource) => {
    const List = RESOURCE_TYPE_COMPONENTS[resource.type]?.List
    return List ? <List {...resource} /> : null
}

const AssistantCard = ({ children, title }) => (
    <Paper
        elevation={0}
        sx={{
            border: '1px solid', borderColor: 'divider', borderRadius: 3,
            p: { xs: 2, sm: 3 }, mb: 2,
        }}
    >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
            <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32 }}>
                <SmartToyIcon fontSize="small" />
            </Avatar>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                {title}
            </Typography>
        </Box>
        {children}
    </Paper>
)

const UserTurn = ({ content }) => (
    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <Paper
            elevation={0}
            sx={{
                bgcolor: 'primary.main', color: 'primary.contrastText',
                px: 2, py: 1.25, borderRadius: 3, maxWidth: '80%',
                display: 'flex', gap: 1, alignItems: 'flex-start',
            }}
        >
            <Typography variant="body1">{content}</Typography>
            <PersonIcon fontSize="small" sx={{ mt: 0.25, opacity: 0.85 }} />
        </Paper>
    </Box>
)

// Live tool-use trace shown while the assistant works (and, collapsed, on the finished turn).
const Trace = ({ steps, done }) => (
    <Box sx={{ mb: done ? 1.5 : 0 }}>
        {steps.map((s, i) => {
            const isActive = !done && i === steps.length - 1
            return (
                <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1.25, py: 0.4 }}>
                    {isActive
                        ? <CircularProgress size={15} thickness={5} />
                        : <CheckCircleIcon sx={{ fontSize: 16, color: 'success.main' }} />}
                    <Typography
                        variant="body2"
                        color={isActive ? 'text.primary' : 'text.secondary'}
                        sx={{ fontWeight: isActive ? 500 : 400 }}
                    >
                        {s.message}{s.detail ? ` — ${s.detail}` : ''}
                    </Typography>
                </Box>
            )
        })}
    </Box>
)

const StudyPlan = ({ turn }) => {
    const byId = {}
    ;(turn.resources || []).forEach((r) => { byId[r.id] = r })
    const steps = (turn.plan && turn.plan.steps) || []

    return (
        <>
            {turn.trace && turn.trace.length > 0 && (
                <Box sx={{ mb: 2 }}>
                    <Trace steps={turn.trace} done />
                    <Divider />
                </Box>
            )}
            <Typography variant="body1" sx={{ mb: steps.length ? 2.5 : 0 }}>
                {turn.content}
            </Typography>

            {steps.map((step, i) => {
                const items = (step.resource_ids || []).map((id) => byId[id]).filter(Boolean)
                const last = i === steps.length - 1
                const stepTitle = cleanTitle(step.title)
                // Hide the step heading when it only repeats its single resource's title (the stub
                // case); a distinct pedagogical label from a real backend still shows.
                const itemTitles = items.map((r) => (r.title || '').trim().toLowerCase())
                const showHeading = Boolean(stepTitle)
                    && !(items.length === 1 && itemTitles.includes(stepTitle.toLowerCase()))
                return (
                    <Box key={i} sx={{ display: 'flex', gap: 2 }}>
                        {/* number badge + connector line = a roadmap rail */}
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                            <Box sx={{
                                width: 30, height: 30, borderRadius: '50%', flexShrink: 0,
                                bgcolor: 'primary.main', color: 'primary.contrastText',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                fontSize: 14, fontWeight: 700,
                            }}>
                                {i + 1}
                            </Box>
                            {!last && <Box sx={{ flexGrow: 1, width: '2px', bgcolor: 'divider', my: 0.5 }} />}
                        </Box>
                        <Box sx={{ flexGrow: 1, minWidth: 0, pb: last ? 0 : 3 }}>
                            {showHeading && (
                                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                    {stepTitle}
                                </Typography>
                            )}
                            {step.rationale && (
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                    {step.rationale}
                                </Typography>
                            )}
                            {items.map((r) => (
                                <Box key={`${r.type}-${r.id}`}>{renderListItem(r)}</Box>
                            ))}
                        </Box>
                    </Box>
                )
            })}
        </>
    )
}

const SignInPrompt = ({ navigate }) => (
    <Paper
        elevation={0}
        sx={{
            border: '1px solid', borderColor: 'divider', borderRadius: 3,
            p: 4, textAlign: 'center', maxWidth: 480, mx: 'auto', mt: 6,
        }}
    >
        <Avatar sx={{ bgcolor: 'primary.main', width: 56, height: 56, mx: 'auto', mb: 2 }}>
            <SmartToyIcon />
        </Avatar>
        <Typography variant="h6" gutterBottom>Sign in to use the AI Learning Assistant</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            The assistant reads your quiz profile — your experience level and interests — to gauge
            your background and tailor a study plan from the hub's resources. Sign in to get started.
        </Typography>
        <Button variant="contained" onClick={() => navigate(PAGES.login.path)}>Sign In</Button>
    </Paper>
)

const Chatbot = () => {
    const user = useSelector((state) => state.user)
    const dispatch = useDispatch()
    const navigate = useNavigate()

    const [turns, setTurns] = useState([])
    const [trace, setTrace] = useState(null)   // live trace steps while streaming, else null
    const [liveReply, setLiveReply] = useState('')  // reply text streaming in token by token
    const [input, setInput] = useState('')
    const [sending, setSending] = useState(false)
    const endRef = useRef(null)

    const isUserSessionLoading = user === null
    const isAnonymous = Boolean(user && user.anon)
    const hasLevel = Boolean(user && user.quiz_experience_tags && user.quiz_experience_tags.length > 0)

    useEffect(() => {
        endRef.current?.scrollIntoView({ behavior: 'smooth' })
    }, [turns, trace, liveReply])

    const addAssistant = (payload, traceSteps) => {
        setTurns((prev) => [...prev, {
            role: 'assistant',
            content: payload.reply,
            plan: payload.study_plan,
            resources: payload.resources || [],
            trace: traceSteps || null,
        }])
        setTrace(null)
        setLiveReply('')
    }

    const send = async (text) => {
        const message = (text ?? input).trim()
        if (!message || sending) return

        const history = turns.map((t) => ({ role: t.role, content: t.content }))
        setTurns((prev) => [...prev, { role: 'user', content: message }])
        setInput('')
        setSending(true)
        setTrace([])
        setLiveReply('')

        const collected = []
        let replyAcc = ''
        let gotDone = false
        try {
            await chatbotService.streamMessage({
                message,
                history,
                onEvent: (ev) => {
                    if (ev.type === 'status') {
                        collected.push({ message: ev.message, detail: traceDetail(ev) })
                        setTrace([...collected])
                    } else if (ev.type === 'token') {
                        replyAcc += ev.text || ''
                        setLiveReply(replyAcc)
                    } else if (ev.type === 'done') {
                        gotDone = true
                        addAssistant(ev.payload, collected.slice())
                    } else if (ev.type === 'error') {
                        throw new Error(ev.message || 'stream error')
                    }
                },
            })
            if (!gotDone) throw new Error('stream ended without a result')
        } catch (streamErr) {
            // Graceful fallback to the non-streaming endpoint if SSE fails.
            try {
                const data = await chatbotService.sendMessage({ message, history })
                addAssistant(data, null)
            } catch (e) {
                dispatch(setErrorNotification('The assistant is unavailable right now. Please try again.'))
                setTurns((prev) => [...prev, {
                    role: 'assistant', content: 'Sorry, something went wrong. Please try again.',
                    plan: null, resources: [], trace: null,
                }])
            }
        } finally {
            setTrace(null)
            setLiveReply('')
            setSending(false)
        }
    }

    const onKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault()
            send()
        }
    }

    const header = (
        <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                <AutoAwesomeIcon color="primary" />
                <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    AI Learning Assistant
                </Typography>
            </Box>
            <Typography variant="body1" color="text.secondary">
                Describe what you want to learn and I'll build a study plan from the hub's resources,
                tailored to your experience level and interests.
            </Typography>
        </Box>
    )

    return (
        <Container maxWidth="md" sx={{ marginTop: '90px', minHeight: '80vh', pb: 8 }}>
            {header}
            <Divider sx={{ mb: 3 }} />

            {isUserSessionLoading && (
                <Typography variant="body1" color="text.secondary">
                    Loading your user session...
                </Typography>
            )}

            {!isUserSessionLoading && isAnonymous && <SignInPrompt navigate={navigate} />}

            {!isUserSessionLoading && !isAnonymous && (
                <>
                    {!hasLevel && (
                        <Alert
                            severity="info"
                            action={<Button color="inherit" size="small" onClick={() => navigate(PAGES.quiz.path)}>Take the Quiz</Button>}
                            sx={{ mb: 3, borderRadius: 2 }}
                        >
                            Take the quiz so I can gauge your experience level and personalize your plan.
                        </Alert>
                    )}

                    {turns.length === 0 && !sending && (
                        <Box sx={{ mb: 3 }}>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                Try one of these to get started:
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                {SUGGESTIONS.map((s) => (
                                    <Chip
                                        key={s}
                                        label={s}
                                        variant="outlined"
                                        color="primary"
                                        clickable
                                        onClick={() => send(s)}
                                        sx={{ height: 'auto', py: 0.75, '& .MuiChip-label': { whiteSpace: 'normal' } }}
                                    />
                                ))}
                            </Box>
                        </Box>
                    )}

                    <Box sx={{ mb: 3 }}>
                        {turns.map((turn, idx) => (
                            turn.role === 'user'
                                ? <UserTurn key={idx} content={turn.content} />
                                : <AssistantCard key={idx} title="AI Learning Assistant"><StudyPlan turn={turn} /></AssistantCard>
                        ))}
                        {trace && (
                            <AssistantCard title={liveReply ? 'AI Learning Assistant' : 'Thinking…'}>
                                {/* Tool stages (if any) show as a live trace; once the reply starts
                                    streaming, the trace collapses to checks and the text types in.
                                    Before either, a typing animation signals the model is thinking. */}
                                {trace.length > 0 && <Trace steps={trace} done={Boolean(liveReply)} />}
                                {liveReply
                                    ? <Box sx={{ mt: trace.length > 0 ? 1.5 : 0 }}><StreamingReply text={liveReply} /></Box>
                                    : (trace.length === 0 && <TypingDots />)}
                            </AssistantCard>
                        )}
                        <div ref={endRef} />
                    </Box>

                    <Box sx={{
                        display: 'flex', gap: 1, alignItems: 'flex-end',
                        position: 'sticky', bottom: 16,
                    }}>
                        <TextField
                            fullWidth
                            multiline
                            maxRows={4}
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            onKeyDown={onKeyDown}
                            placeholder="What would you like to learn?"
                            disabled={sending}
                            sx={{
                                bgcolor: 'background.paper',
                                '& .MuiOutlinedInput-root': { borderRadius: 3 },
                            }}
                        />
                        <Button
                            variant="contained"
                            endIcon={<SendIcon />}
                            onClick={() => send()}
                            disabled={sending || !input.trim()}
                            sx={{ height: 56, borderRadius: 3, whiteSpace: 'nowrap' }}
                        >
                            Send
                        </Button>
                    </Box>
                </>
            )}
        </Container>
    )
}

export default Chatbot
